<template>
  <section>
    <v-data-table
      class="safety-elevation-3 table-striped"
      :headers="headers"
      :items="items"
      hide-default-footer
      :items-per-page="-1"
      fixed-header
      group-by="minGroupsOrderIndex"
      multi-sort
      :sort-by="['minGroupsOrderIndex', 'group', 'dueCombined']"
      :sort-desc="[false, false, false]"
      :loading="loading"
      ref="tableClosedHeader"
    >
      <template v-slot:[`group.header`]="{ items, isOpen, toggle }">
        <v-hover v-slot="{ hover }">
          <th
            @click="toggle"
            colspan="11"
            :style="{
              backgroundColor: hover ? '#e0e0e0' : '#eeeeee',
              cursor: hover ? 'pointer' : 'auto',
            }"
          >
            <v-row>
              <v-col cols="auto">
                <v-btn small class="primary mr-5 font" style="cursor: pointer;">
                  {{ isOpen ? $t("dialog.hide") : $t("dialog.open") }}
                  <v-icon style="margin-right: -9px;">
                    {{ isOpen ? "mdi-menu-down" : "mdi-menu-right" }}
                  </v-icon>
                </v-btn>
                <span
                  v-if="items.length"
                  class="body-2 font-weight-medium text--secondary"
                  >{{ items[0].groupName }}</span
                >
              </v-col>
              <v-col>
                <v-chip
                  v-if="countConfirmed(items) > 0"
                  small
                  style="cursor: pointer;"
                  color="green lighten-4"
                  class="success--text text--darken-1 text-uppercase mr-1 font-weight-regular"
                >
                  {{ countConfirmed(items) }}
                </v-chip>
                <v-chip
                  v-if="countIsPending(items) > 0"
                  small
                  style="cursor: pointer;"
                  color="orange lighten-4"
                  class="warning--text text--darken-1 text-uppercase mr-1 font-weight-regular"
                >
                  {{ countIsPending(items) }}
                </v-chip>
                <v-chip
                  v-if="countIsRereadableAndOverDue(items) > 0"
                  small
                  style="cursor: pointer;"
                  color="deep-purple lighten-4"
                  class="error--text text--darken-1 text-uppercase mr-1 font-weight-regular"
                >
                  {{ countIsRereadableAndOverDue(items) }}
                </v-chip>
                <v-chip
                  v-if="countOverdue(items) > 0"
                  small
                  style="cursor: pointer;"
                  color="deep-orange lighten-4"
                  class="error--text text--darken-1 text-uppercase mr-1 font-weight-regular"
                >
                  {{ countOverdue(items) }}
                </v-chip>
              </v-col>
            </v-row>
          </th>
        </v-hover>
      </template>

      <template v-slot:[`header.menu`]>
        <v-menu bottom left v-if="!onlyConfirmable">
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              v-if="role('admin', 'safetyRepresentative')"
              v-on="on"
              :disabled="
                !items.some((item) => item.signature || item.confirmation)
              "
            >
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item @click="revokeAll(null)">
              <v-icon small left>remove_circle_outline</v-icon>
              {{ $t("instructions.revokeAll.revoke.title") }}
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:[`item.code`]="{ item }"
        >{{ item.code }}
        {{
          item.isAssigned ? `(${$t("instructions.assigned")})` : ""
        }}</template
      >
      <template
        v-if="me && employee.id === me.id"
        v-slot:[`item.name`]="{ item }"
      >
        {{ item.name }}
      </template>

      <template v-else v-slot:[`item.name`]="{ item }">
        <a target="blank" :href="item.localizedFile.file.url">
          {{ item.name }}
        </a>
      </template>

      <template v-slot:[`item.employeeTagsGroups`]="{ item }">
        <template v-for="tagsGroup in item.employeeTagsGroups">
          <v-chip v-for="tag in tagsGroup" small>
            {{ tag.description ? tag.description : tag.name }}
          </v-chip>
        </template>
      </template>

      <template v-slot:[`item.groups`]="{ item }">
        <template v-for="group in item.groups">
          <v-chip small :key="`gc${group.id}`" class="ml-1 mb-1">
            {{ group.name }}
          </v-chip>
        </template>
      </template>

      <template v-slot:[`item.revision`]="{ item }"
        >{{ item.revision }} ({{ item.date | date }})</template
      >

      <template v-slot:[`item.duration`]="{ item }">{{
        item.duration | minutesHours
      }}</template>

      <template v-slot:[`item.group`]="{ item }">
        <v-chip
          v-if="!item.signature && !item.confirmation && item.isSignable"
          :color="dueDateColor(item.isDue)"
          :class="item.isDue ? 'error--text' : 'warning--text'"
          class="text-uppercase text--darken-1"
          small
        >
          {{ item.dueDateTime | date }}
        </v-chip>

        <v-chip
          v-else-if="item.signature && !item.isRereadable && item.isSignable"
          small
          color="green lighten-4"
          class="success--text text--darken-1 text-uppercase"
        >
          ✔
        </v-chip>

        <v-chip
          v-else-if="item.signature && item.isRereadable"
          small
          :color="dueRereadingColor(item.isRereadingDue)"
          :class="item.isRereadingDue ? 'error--text' : 'warning--text'"
          class="text--darken-1 text-uppercase"
        >
          {{ item.rereadingDueDate | date }}
        </v-chip>
        <div
          v-if="
            item.latestRevoke &&
              !(item.signature && !item.isRereadable) &&
              item.isSignable
          "
        >
          {{ item.latestRevoke.reason }}
        </div>
      </template>

      <template v-slot:[`item.signatureDate`]="{ item }">
        <span v-if="!item.isSignable"></span>
        <span v-else-if="item.signature">{{ item.signature.date | date }}</span>
        <span v-else>-</span>
        <div
          v-if="
            item.markedAsRead &&
              (role(
                'admin',
                'departmentManager',
                'manager',
                'safetyRepresentative'
              ) ||
                isManager(employee, me))
          "
        >
          <a
            target="blank"
            :href="`/employees/${item.markedAsRead.whoMarkedId}`"
          >
            ({{ item.markedAsRead.whoMarkedId }})
          </a>
        </div>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <!-- read & sign -->
        <v-btn
          v-if="me && me.id == employee.id"
          :to="{
            name: 'instruction',
            params: {
              id: item.localizedFile.file.id,
            },
            query: {
              url: item.localizedFile.file.url,
              testId: item.testId,
              signed: !!item.signature,
              signatureId: item.signature && item.signature.id,
              confirmationId: item.confirmation && item.confirmation.id,
              isRereadable: item.isRereadable,
              title: `${item.code} — ${item.name}`,
              versionId: item.versionId,
              isSignable: item.isSignable,
            },
          }"
          :class="
            item.noActionRequired
              ? 'mr-2'
              : 'mr-2 primary accent-4 white--text '
          "
          small
          :text="item.noActionRequired"
        >
          {{
            item.localizedFile.file.extension == ".pdf"
              ? $t("instructions.read")
              : $t("instructions.watch")
          }}
          {{ item.noActionRequired ? "" : $t("instructions.andSign") }}
        </v-btn>

        <!-- confirm signature -->
        <v-btn
          v-if="
            item.signature &&
              !item.confirmation &&
              role(
                'admin',
                'departmentManager',
                'manager',
                'safetyRepresentative'
              )
          "
          @click="confirm(item.signature)"
          class="primary accent-4 white--text my-1"
          small
        >
          {{ $t("instructions.confirm.create.action") }}
        </v-btn>
      </template>

      <template v-slot:[`item.menu`]="{ item }">
        <v-menu bottom left>
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              v-on="on"
              :disabled="
                !(
                  role(
                    'admin',
                    'departmentManager',
                    'manager',
                    'safetyRepresentative'
                  ) || isManager(employee, me)
                )
              "
            >
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list>
            <!-- mark as read as manager -->
            <v-list-item
              v-if="
                item.isSignable &&
                  (role('safetyRepresentative') || isManager(employee, me)) &&
                  (!item.signature || item.isRereadable)
              "
              @click="markInstructionAsRead(item)"
            >
              {{ $t("instructions.markAsRead.create.action") }}
            </v-list-item>
            <!-- deactivate instruction mark -->
            <v-list-item
              v-if="
                item.markedAsRead &&
                  (role('safetyRepresentative') ||
                    (me && me.id == item.markedAsRead.whoMarkedId))
              "
              @click="deactivateInstructionMark(item.markedAsRead.id, false)"
              >{{ $t("instructions.markAsRead.deactivate.action") }}
            </v-list-item>
            <!-- revoke signature -->
            <v-list-item
              v-if="
                item.signature &&
                  !item.confirmation &&
                  role(
                    'admin',
                    'departmentManager',
                    'manager',
                    'safetyRepresentative'
                  )
              "
              @click="revoke(item.signature)"
            >
              <v-icon small left>remove_circle_outline</v-icon>
              {{ $t("instructions.sign.revoke.title") }}
            </v-list-item>

            <!-- revoke confirmation -->
            <v-list-item
              v-if="
                item.confirmation &&
                  item.confirmation.id &&
                  role(
                    'admin',
                    'departmentManager',
                    'manager',
                    'safetyRepresentative'
                  )
              "
              @click="revoke(item.confirmation)"
            >
              <v-icon small left>remove_circle_outline</v-icon>
              {{ $t("instructions.confirm.revoke.title") }}
            </v-list-item>

            <!-- force rereading -->
            <v-list-item
              v-if="
                item.isSignable &&
                  item.signature &&
                  !item.isRereadable &&
                  role(
                    'admin',
                    'departmentManager',
                    'manager',
                    'safetyRepresentative'
                  )
              "
              @click="forceRereading(item)"
            >
              {{ $t("instructions.forceRereading.button") }}
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:no-data>{{
        $t("profile.instructions.missing")
      }}</template>
    </v-data-table>

    <revoke-modal
      v-if="revokeDialog.type"
      :type="revokeDialog.type"
      :show="revokeDialog.show"
      :title="$t(`instructions.${revokeDialog.type}.revoke.title`)"
      :text="
        $t(`instructions.${revokeDialog.type}.revoke.text`) + revokeDetailsText
      "
      :action="$t(`instructions.${revokeDialog.type}.revoke.action`)"
      @confirm="revokeDialog.callback"
      @cancel="revokeDialog.show = false"
    />

    <mark-as-read-dialog
      v-if="markDialog.show"
      :show="markDialog.show"
      :title="$t('instructions.markAsRead.create.title')"
      :text="`${$t('instructions.markAsRead.create.text')}`"
      :action="$t('instructions.markAsRead.create.action')"
      @confirm="markDialog.callback"
      @cancel="markDialog.show = false"
    />

    <force-rereading-dialog
      :show="forceRereadingDialog.show"
      :instruction="forceRereadingDialog.instruction"
      :employee="employee"
      @hide="forceRereadingDialog.show = false"
      @refresh="$emit('refresh')"
    />
  </section>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import Impersonation from "@/components/Impersonation";
import RevokeModal from "@/components/RevokeModal";
import MarkAsReadDialog from "@/components/instruction/MarkAsReadDialog";
import ForceRereadingDialog from "@/components/instruction/ForceRereadingDialog";
import ConfirmSignatureQuery from "@/graphql/ConfirmSignature.gql";
import RevokeSignatureQuery from "@/graphql/RevokeSignature.gql";
import SetReadingDurationQuery from "@/graphql/SetReadingDuration.gql";
import MarkInstructionAsRead from "@/graphql/mutation/instruction/MarkInstructionAsRead.gql";
import DeactivateInstructionMark from "@/graphql/mutation/instruction/DeactivateInstructionMark.gql";
import RereadingRevokeQuery from "@/graphql/RereadingRevokeWithoutPassword.gql";

export default {
  components: {
    Impersonation,
    RevokeModal,
    MarkAsReadDialog,
    ForceRereadingDialog,
  },

  mounted() {
    let tableClosedHeader = this.$refs.tableClosedHeader;
    let groupMinOrderIndexes = Object.keys(
      tableClosedHeader.$vnode.componentInstance.openCache
    );

    groupMinOrderIndexes.forEach((orderIndex) => {
      const groupItems = this.items.filter(
        (x) => x.minGroupsOrderIndex == orderIndex
      );
      if (
        this.countIsPending(groupItems) === 0 &&
        this.countIsRereadableAndOverDue(groupItems) === 0 &&
        this.countOverdue(groupItems) === 0 &&
        this.items.length > 15
      ) {
        tableClosedHeader.$vnode.componentInstance.openCache[
          orderIndex
        ] = false;
      } else {
        tableClosedHeader.$vnode.componentInstance.openCache[orderIndex] = true;
      }
    });
  },

  props: {
    employee: {
      type: Object,
      required: true,
    },
    instructions: {
      type: Array,
      required: true,
    },
    tags: {
      type: Array,
      required: true,
    },
    modes: {
      type: Array,
      required: true,
    },
    instructionGroupFilter: {
      type: Array,
      required: true,
    },
    onlyConfirmable: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      revokeDetailsText: "",
      revokeDialog: {
        show: false,
        type: null,
        callback: () => {},
      },
      markDialog: {
        show: false,
        callback: () => {},
      },
      forceRereadingDialog: {
        show: false,
        instruction: null,
      },
    };
  },

  computed: {
    ...mapGetters(["role"]),
    ...mapState(["me"]),
    headers() {
      const h = [
        {
          text: this.$i18n.t("instructions.column.code"),
          value: "code",
          sortable: false,
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("instructions.column.name"),
          value: "name",
          sortable: false,
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("instructions.column.groups"),
          value: "groups",
          sortable: false,
          align: " d-none",
          class: "text-uppercase subheading text-truncate",
          filter: (value) => {
            if (
              !this.instructionGroupFilter ||
              !this.instructionGroupFilter.length
            )
              return true;
            return (
              value &&
              value.length &&
              value
                .map((g) => g.name)
                .some((g) => this.instructionGroupFilter.includes(g))
            );
          },
        },
        {
          text: this.$i18n.t("instructions.column.revision"),
          value: "revision",
          sortable: false,
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("instructions.column.tags"),
          value: "employeeTagsGroups",
          sortable: false,
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("instructions.column.duration"),
          value: "duration",
          sortable: false,
          class: "text-uppercase subheading text-truncate",
          align: "right",
        },
        {
          text: this.$i18n.t("instructions.column.signatureDate"),
          value: "signatureDate",
          sortable: false,
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("instructions.column.status"),
          sortable: false,
          value: "group",
          class: "text-uppercase subheading text-truncate",
        },
        {
          text: this.$i18n.t("profile.mentorship.column.action"),
          value: "actions",
          sortable: false,
          class: "text-uppercase subheading text-truncate",
        },
        { text: "", value: "menu", sortable: false, align: "right" },
      ];
      if (this.role("employee")) {
        h.forEach((header, i) => {
          if (header.value == "duration" || header.value == "menu") {
            h.splice(i, 1);
          }
        });
      }
      if (this.onlyConfirmable) {
        h.forEach((header, i) => {
          if (header.value == "confirmation") {
            h.splice(i, 1);
          }
        });
      }

      return h;
    },
    items() {
      return (
        (this.instructions &&
          this.instructions.map((i) => {
            const version = i.version;
            let signature = version.signatures.find(
              (s) =>
                s.type === "sign" &&
                !s.signatures.find((ss) => ss.type === "revoke")
            );

            let confirmation =
              signature &&
              signature.signatures.find(
                (s) =>
                  s.type === "confirm" &&
                  !s.signatures.find((ss) => ss.type === "revoke")
              );

            let latestRevoke =
              !signature &&
              version.signatures.reduce(
                (latest, s) =>
                  s.type === "sign"
                    ? s.signatures.find((ss) => ss.type === "revoke") || latest
                    : latest,
                null
              );

            if (version.markedAsRead && !signature) {
              const marked = this.markSignatureAndConfirmation(
                version.markedAsRead
              );

              signature = marked.signature;
              confirmation = marked.confirmation;
              version.duration = null;
            }
            const rereadingInDays = version.rereadingInDays;

            const rereadingDueMomentObj =
              (rereadingInDays &&
                signature &&
                this.$moment(signature.date).add(rereadingInDays, "days")) ||
              null;

            const rereadingDueDate =
              (rereadingDueMomentObj && rereadingDueMomentObj.format()) || "";

            const employeeTagsGroups = [];
            i.tagsGroups.forEach((group) => {
              const filteredTags = this.tags.filter((tag) =>
                group.find((t) => t.id === tag.id)
              );
              employeeTagsGroups.push(filteredTags);
            });

            const dueDateTime = this.$moment(version.dueDate).format();
            const isDue =
              i.isSignable &&
              this.$moment(version.dueDate).isBefore(this.$moment());

            const isRereadingDue =
              (rereadingDueMomentObj &&
                this.$moment(rereadingDueMomentObj).isBefore(this.$moment())) ||
              false;

            const isRereadable =
              i.isSignable &&
              rereadingInDays &&
              !!signature &&
              this.isRereadable(rereadingDueDate);

            const group = (function() {
              if (signature) {
                if (confirmation) {
                  if (!rereadingInDays) {
                    return 3;
                  } else if (isRereadable) {
                    return 1;
                  } else {
                    return 2;
                  }
                } else if (rereadingInDays && isRereadable) {
                  return 1;
                } else return 2;
              } else return 1;
            })();

            const noActionRequired =
              !!(signature && !rereadingInDays) ||
              !!(signature && !isRereadable) ||
              !!!i.isSignable;

            // for sorting
            const dueCombined = (() => {
              if (signature) {
                if (confirmation) {
                  if (!rereadingInDays) {
                    return "";
                  } else {
                    return rereadingDueDate;
                  }
                }
                return rereadingInDays ? rereadingDueDate : dueDateTime;
              }
              return dueDateTime;
            })();

            const name = version.localizedFile.name;
            const versionId = version.id;

            const minGroupsOrderIndex = i.groups.reduce((acc, group) => {
              if (group.orderIndex < acc) {
                acc = group.orderIndex;
              }
              return acc;
            }, 999);
            let groupName =
              (i.groups && i.groups.length && i.groups[0].name) || "";
            return {
              ...{
                code: i.code,
                name,
                employeeTagsGroups: employeeTagsGroups,
                tagsGroups: i.tagsGroups,
                groups: i.groups,
                testId: i.testId,
                isAssigned: i.isAssigned,
                isSignable: i.isSignable,
                ...version,
              },
              id: i.id,
              minGroupsOrderIndex,
              groupName,
              versionId,
              signature,
              confirmation,
              latestRevoke,
              isDue,
              dueDateTime,
              dueCombined,
              isRereadingDue,
              isRereadable,
              noActionRequired,
              group,
              rereadingDueDate,
            };
          })) ||
        []
      );
    },
  },

  methods: {
    isRereadable(rereadingDueDate) {
      return this.$moment().isAfter(
        this.$moment(rereadingDueDate).add(-30, "day")
      );
    },
    confirm(signature) {
      this.$apollo
        .mutate({
          mutation: ConfirmSignatureQuery,
          variables: {
            signatureId: signature.id,
          },
        })
        .then((res) => {
          signature.signatures = res.data.signature.signatures;
          if (this.onlyConfirmable) {
            this.$emit("refresh");
          }
        })
        .catch((err) => {
          let errorMessage = err.message;
          if (err.message.includes("StatusConflict")) {
            errorMessage =
              "Already confirmed or signature is revoked. Please reload.";
          }
          this.$store.commit("push_global_message", {
            text: errorMessage,
            color: "warning",
          });
        })
        .finally(() => {
          if (
            this.modes &&
            this.modes.length &&
            this.items.every((i) => i.confirmation)
          ) {
            this.$store.commit("push_global_message", {
              text: this.$i18n.t("profile.instructions.modeItemsConfirmed"),
              color: "success",
            });
          }
        });
    },

    async revoke(signature, args) {
      if (!args) {
        this.revokeDetailsText = "";
        this.revokeDialog = {
          callback: (args) => this.revoke(signature, args),
          type: signature.type,
          show: true,
        };
      } else {
        this.revokeDialog = {
          callback: () => {},
          type: null,
          show: false,
        };

        try {
          this.loading = true;
          await this.revokeMutation(signature, args);
        } finally {
          this.loading = false;
        }
      }
    },

    forceRereading(item) {
      this.forceRereadingDialog = {
        instruction: item,
        show: true,
      };
    },

    revokeMutation(signature, args) {
      return this.$apollo
        .mutate({
          mutation: RevokeSignatureQuery,
          variables: {
            signatureId: signature.id,
            reason: args.reason,
          },
        })
        .then((res) => {
          signature.signatures = res.data.signature.signatures;
          if (signature.type == "sign") {
            this.annulateReadingTime(
              signature && signature.employee && signature.employee.id,
              signature && signature.fileId
            );
          }
        });
    },

    deactivateInstructionMark(id, skipRefresh) {
      return this.$apollo
        .mutate({
          mutation: DeactivateInstructionMark,
          variables: {
            id,
          },
        })
        .then(() => {
          if (!skipRefresh) {
            this.$emit("refresh");
          }
        })
        .catch(() => {
          this.$store.commit("push_global_message", {
            text: `Failed to disable instruction mark with id ${id}.`,
            color: "error",
          });
        });
    },

    markInstructionAsRead(item, args) {
      if (!args) {
        this.markDialog = {
          callback: (args) => this.markInstructionAsRead(item, args),
          show: true,
        };
      } else {
        this.markDialog = {
          callback: () => {},
          show: false,
        };

        let promise;

        if (item.signature && item.isRereadable && !item.markedAsRead) {
          promise = this.rereadingRevoke(
            item.signature && item.signature.id,
            item.confirmation && item.confirmation.id
          );
        } else if (item.markedAsRead) {
          promise = this.deactivateInstructionMark(item.markedAsRead.id, true);
        } else {
          promise = Promise.resolve();
        }

        promise
          .then(() => {
            return this.$apollo.mutate({
              mutation: MarkInstructionAsRead,
              variables: {
                employeeId: this.employee.id,
                versionId: item.versionId,
                reason: args.reason,
                readDate: args.readDate,
              },
            });
          })
          .then(() => {
            this.$emit("refresh");
          })
          .catch(() => {
            this.$store.commit("push_global_message", {
              text: `Failed to mark as read for employee id ${this.employee.id} and version id ${item.code}.`,
              color: "error",
            });
          });
      }
    },

    rereadingRevoke(signatureId, confirmationId) {
      return this.$apollo
        .mutate({
          mutation: RereadingRevokeQuery,
          variables: {
            confirmationId,
            signatureId,
          },
        })
        .catch(() => {
          this.$store.commit("push_global_message", {
            text: `Failed to revoke previous signature.`,
            color: "error",
          });
        });
    },

    markSignatureAndConfirmation(markedAsRead) {
      const signature = {
        id: null,
        date: markedAsRead.readDate || markedAsRead.dateTime,
        type: "sign",
        reason: null,
        fileId: null,
        employee: {
          id: markedAsRead.whoMarkedId,
        },
      };
      const confirmation = { ...signature };
      confirmation.type = "confirm";

      return { signature, confirmation };
    },

    getSignatureInfoText(i) {
      return `${i.code}(${i.revision}) ${this.$t(
        "profile.history.signature." + i.signature.type
      )} ${this.$options.filters.nameId(
        i.signature.employee
      )} ${this.$options.filters.date(i.signature.date)}<br>`;
    },

    getConfirmationInfoText(i) {
      return `${i.code}(${i.revision}) ${this.$t(
        "profile.history.signature." + i.confirmation.type
      )} ${this.$options.filters.nameId(
        i.confirmation.employee
      )} ${this.$options.filters.date(i.confirmation.date)}<br>`;
    },

    async revokeAll(args) {
      const instructionsSignedOrConfirmed = this.items.filter(
        (i) => i.signature || i.confirmation
      );

      let revokeDetailsText = "<br>";
      instructionsSignedOrConfirmed.forEach((i) => {
        if (i.confirmation) {
          revokeDetailsText += this.getConfirmationInfoText(i);
        }
        if (i.signature) {
          revokeDetailsText += this.getSignatureInfoText(i);
        }
      });

      if (!args) {
        this.revokeDetailsText = revokeDetailsText;
        this.revokeDialog = {
          callback: (args) => this.revokeAll(args),
          type: "revokeAll",
          show: true,
        };

        return;
      }

      this.revokeDialog = {
        callback: () => {},
        type: null,
        show: false,
      };

      try {
        this.loading = true;
        await this.revokeItems(instructionsSignedOrConfirmed, args);
      } finally {
        this.loading = false;
        this.revokeDetailsText = "";
      }
    },

    async revokeItems(items, args) {
      let successfullyRevokedCount = 0;
      const toRevokeCount = items.reduce((acc, item) => {
        if (item.confirmation) {
          acc++;
        }
        if (item.signature) {
          acc++;
        }
        return acc;
      }, 0);

      for (const item of items) {
        if (item.confirmation) {
          await this.revokeMutation(item.confirmation, args);
          successfullyRevokedCount++;
        }
        if (item.signature) {
          await this.revokeMutation(item.signature, args);
          successfullyRevokedCount++;
        }
      }

      this.$store.commit("push_global_message", {
        text: `${successfullyRevokedCount}/${toRevokeCount} ${this.$t(
          "instructions.revokeAll.status"
        )}`,
        color: "success",
      });
    },

    annulateReadingTime(employeeId, fileId) {
      if (!employeeId || !fileId) {
        return;
      }
      this.$apollo
        .mutate({
          mutation: SetReadingDurationQuery,
          variables: {
            employeeId: employeeId,
            fileId: fileId,
            duration: 0,
          },
        })
        .then(() => {
          this.$emit("refresh");
        })
        .catch((err) =>
          this.$store.commit("push_global_message", {
            text: `Failed to annulate reading time for employee id ${employeeId} and file id ${fileId} with error: ${err}`,
            color: "error",
          })
        );
    },

    dueDateColor(isDue) {
      return isDue ? "deep-orange lighten-4" : "orange lighten-4";
    },
    dueRereadingColor(isDue) {
      return isDue ? "deep-purple lighten-4" : "orange lighten-4";
    },

    countIsPending(groupItems) {
      return groupItems.filter(
        (item) =>
          item.isSignable &&
          ((!item.signature && !item.isDue) ||
            (item.isRereadable && item.signature && !item.isRereadingDue))
      ).length;
    },

    countConfirmed(groupItems) {
      return groupItems.filter(
        (item) =>
          item.isSignable &&
          !item.isRereadable &&
          item.signature &&
          item.confirmation
      ).length;
    },

    countIsRereadableAndOverDue(groupItems) {
      return groupItems.filter(
        (item) => item.signature && item.isRereadable && item.isRereadingDue
      ).length;
    },

    countOverdue(groupItems) {
      return groupItems.filter(
        (item) => !item.isRereadable && item.isDue && !item.signature
      ).length;
    },

    isManager(employee, me) {
      return employee && employee.manager && me && employee.manager.id == me.id;
    },
  },
};
</script>
